import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ScoreBars from '@/components/feature/ScoreBars';
import { api, API_BASE } from '@/api/client';
import type {
  CandidateDetail as CandidateDetailType,
  InterviewInvite,
  InterviewSlot,
  ScoreBlock,
  Vacancy,
} from '@/types';

const statusFlow = [
  { key: 'new', label: 'Новый' },
  { key: 'resume_scored', label: 'Резюме оценено' },
  { key: 'form_sent', label: 'Письмо отправлено' },
  { key: 'answers_received', label: 'Ответы получены' },
  { key: 'answers_scored', label: 'Анкета оценена' },
  { key: 'interview_scheduled', label: 'Интервью' },
  { key: 'rejected', label: 'Отказ' },
];

const statusLabels: Record<string, string> = Object.fromEntries(statusFlow.map((s) => [s.key, s.label]));

const statusBadgeColors: Record<string, string> = {
  new: 'bg-background-100 text-foreground-600',
  resume_scored: 'bg-purple-50 text-purple-700',
  form_sent: 'bg-secondary-100 text-secondary-700',
  answers_received: 'bg-accent-100 text-accent-700',
  answers_scored: 'bg-emerald-50 text-emerald-700',
  interview_scheduled: 'bg-amber-50 text-amber-700',
  rejected: 'bg-red-50 text-red-600',
};

const compatLabels: Record<string, string> = {
  work_style: 'Стиль работы',
  communication: 'Коммуникация',
  values: 'Ценности',
  growth: 'Рост',
  resilience: 'Стрессоустойчивость',
};

const tagLabels: Record<string, string> = { subdomain: 'Лендинг' };

type TabKey = 'resume' | 'questionnaire' | 'compatibility' | 'chat' | 'emails';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'resume', label: 'Резюме', icon: 'ri-file-text-line' },
  { key: 'questionnaire', label: 'Анкета', icon: 'ri-questionnaire-line' },
  { key: 'compatibility', label: 'Совместимость', icon: 'ri-team-line' },
  { key: 'chat', label: 'Чат hh.ru', icon: 'ri-chat-3-line' },
  { key: 'emails', label: 'Письма', icon: 'ri-mail-send-line' },
];

interface ChatMessage { id: string; author: string; text: string; sent_at: string | null }
interface EmailLog { type: string; status: string; sent_at?: string; created_at?: string }
interface CompatBlock extends ScoreBlock {
  profile_name?: string | null;
  employer_profile_id?: string | null;
}

interface Scoring {
  resume_score: ScoreBlock | null;
  answers_score: ScoreBlock | null;
  compatibility_score: ScoreBlock | null;
  compatibility_scores?: CompatBlock[];
  total: number;
  max_total: number;
}

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('resume');
  const [resumeOpening, setResumeOpening] = useState(false);
  const [c, setC] = useState<CandidateDetailType | null>(null);
  const [scoring, setScoring] = useState<Scoring | null>(null);
  const [vacancy, setVacancy] = useState<Vacancy | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [emailLog, setEmailLog] = useState<EmailLog[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [hrSaving, setHrSaving] = useState(false);
  const [hrSaved, setHrSaved] = useState(false);
  const [hrDraft, setHrDraft] = useState('none');
  const [meetingDraft, setMeetingDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [interview, setInterview] = useState<InterviewSlot | null>(null);
  const [invite, setInvite] = useState<InterviewInvite | null>(null);
  const [inviting, setInviting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const loadAll = useCallback(async () => {
    if (!id) return;
    try {
      const cand = await api.get<CandidateDetailType & { vacancy_id: string }>(`/candidates/${id}`);
      setC(cand);
      setHrDraft(cand.hr_status || 'none');
      setNoteDraft(cand.hr_note || '');
      if (cand.meeting_at) {
        const d = new Date(cand.meeting_at);
        const pad = (n: number) => String(n).padStart(2, '0');
        setMeetingDraft(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      }
      const [sc, vac, chat, emails, slot] = await Promise.all([
        api.get<Scoring>(`/scoring/${id}`).catch(() => null),
        api.get<Vacancy>(`/vacancies/${cand.vacancy_id}`).catch(() => null),
        api.get<ChatMessage[]>(`/candidates/${id}/hh-chat`).catch(() => []),
        api.get<EmailLog[]>(`/emails/log?candidate_id=${id}`).catch(() => []),
        api.get<InterviewSlot | null>(`/candidates/${id}/interview`).catch(() => null),
      ]);
      setScoring(sc);
      setVacancy(vac);
      setChatMessages(chat || []);
      setEmailLog(emails || []);
      setInterview(slot);
    } catch (e) {
      setError((e as Error).message || 'Кандидат не найден');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (id) api.post(`/candidates/${id}/viewed`, {}).catch(() => {});
  }, [id]);

  // Открываем первую вкладку с данными
  useEffect(() => {
    if (!c || !scoring) return;
    if (!scoring.resume_score && !c.resume_text) {
      if (c.answers || scoring.answers_score) setActiveTab('questionnaire');
      else if (scoring.compatibility_score) setActiveTab('compatibility');
    }
  }, [c, scoring]);

  const handleStatusChange = async (newStatus: string) => {
    if (!c) return;
    const prev = c.status;
    setC({ ...c, status: newStatus });
    try {
      await api.put(`/candidates/${id}/status`, { status: newStatus });
    } catch {
      setC({ ...c, status: prev });
    }
  };

  const reloadChat = async () => {
    setSyncing(true);
    try {
      await api.post(`/candidates/${id}/hh-chat/sync`, {});
      const chat = await api.get<ChatMessage[]>(`/candidates/${id}/hh-chat`);
      setChatMessages(chat || []);
    } catch { /* ignore */ }
    setSyncing(false);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api.post(`/candidates/${id}/hh-chat/send`, { text });
      setChatInput('');
      const chat = await api.get<ChatMessage[]>(`/candidates/${id}/hh-chat`);
      setChatMessages(chat || []);
    } catch (err) {
      alert((err as Error).message || 'Не удалось отправить');
    }
    setSending(false);
  };

  const saveHrDecision = async () => {
    if (hrDraft === 'meeting' && !meetingDraft) { alert('Укажите дату и время встречи'); return; }
    setHrSaving(true);
    try {
      await api.put(`/candidates/${id}/hr-status`, {
        hr_status: hrDraft,
        meeting_at: hrDraft === 'meeting' ? new Date(meetingDraft).toISOString() : null,
        hr_note: noteDraft,
      });
      const cand = await api.get<CandidateDetailType & { vacancy_id: string }>(`/candidates/${id}`);
      setC(cand);
      setHrSaved(true);
      setTimeout(() => setHrSaved(false), 2500);
    } catch (err) {
      alert((err as Error).message || 'Ошибка сохранения решения');
    }
    setHrSaving(false);
  };

  const handleInvite = async (sendEmail: boolean) => {
    setInviting(true);
    setLinkCopied(false);
    try {
      const res = await api.post<InterviewInvite>(`/candidates/${id}/interview-invite`, {
        send_email: sendEmail,
      });
      setInvite(res);
      if (!sendEmail) {
        try {
          await navigator.clipboard.writeText(res.link);
          setLinkCopied(true);
        } catch { /* буфер недоступен — ссылка показана ниже */ }
      }
      const cand = await api.get<CandidateDetailType & { vacancy_id: string }>(`/candidates/${id}`);
      setC(cand);
    } catch (err) {
      alert((err as Error).message || 'Не удалось создать приглашение');
    }
    setInviting(false);
  };

  const handleCancelInterview = async () => {
    if (!interview) return;
    if (!confirm('Отменить встречу? Кандидату уйдёт письмо, окно освободится.')) return;
    setInviting(true);
    try {
      await api.post(`/interview-slots/${interview.id}/cancel`, {});
      await loadAll();
    } catch (err) {
      alert((err as Error).message || 'Не удалось отменить');
    }
    setInviting(false);
  };

  const handleRescore = async () => {
    const typeByTab: Record<string, string> = { resume: 'resume', questionnaire: 'answers', compatibility: 'compatibility' };
    const type = typeByTab[activeTab] || 'resume';
    setRescoring(true);
    try {
      await api.post(`/scoring/rescore/${id}/${type}`, {});
      const sc = await api.get<Scoring>(`/scoring/${id}`);
      setScoring(sc);
    } catch (err) {
      alert((err as Error).message || 'Ошибка скоринга');
    }
    setRescoring(false);
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  if (error || !c) {
    return <div className="p-8 text-sm text-foreground-500">{error || 'Кандидат не найден'}</div>;
  }

  const currentStatusIndex = statusFlow.findIndex((s) => s.key === c.status);
  const rS = scoring?.resume_score?.total_score ?? null;
  const qS = scoring?.answers_score?.total_score ?? null;
  const compS = scoring?.compatibility_score?.total_score ?? null;
  const totalScore = (rS || 0) + (qS || 0) + (compS || 0);
  const questions = vacancy?.question_criteria || [];
  const resumeCriteria = vacancy?.resume_criteria || [];
  // Токен живёт час: зашитый в ссылку он протухает на открытой странице.
  // Поэтому запрашиваем файл в момент клика — api-клиент сам обновит токен.
  const openResume = async () => {
    if (resumeOpening) return;
    setResumeOpening(true);
    try {
      const res = await api.raw(`/candidates/${id}/resume`, { method: 'GET' });
      if (!res.ok) throw new Error(`Не удалось открыть резюме (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert((e as Error).message || 'Не удалось открыть резюме');
    }
    setResumeOpening(false);
  };
  const canRescore = activeTab === 'resume' || activeTab === 'questionnaire' || activeTab === 'compatibility';
  const answersCount = Object.keys(c.answers || {}).length;
  const personalQuestions = ((c as unknown as {
    personal_questions?: { id: string; question: string; reason?: string; eval_criteria?: string }[];
  }).personal_questions) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
        <div className="flex items-start justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-foreground-500 hover:text-foreground-700 flex items-center gap-1 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            К кандидатам
          </button>
          <div className="flex items-center gap-2">
            <span className={`inline-flex text-xs font-medium px-3 py-1 rounded-full ${statusBadgeColors[c.status] || statusBadgeColors.new}`}>
              {statusLabels[c.status] || c.status}
            </span>
            <select
              value={c.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="text-xs border border-background-200 rounded-lg px-2 py-1 bg-background-50 text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              {statusFlow.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
            <span className="text-primary-700 font-heading font-bold text-lg">{c.name.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{c.name}</h1>
              <span className="text-sm text-foreground-500">{c.position}</span>
              {vacancy?.title && (
                <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded">
                  {vacancy.title}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {c.email && (
                <a href={`mailto:${c.email}`} className="text-xs flex items-center gap-1 text-foreground-600 bg-background-50 px-2.5 py-1 rounded-lg border border-background-200 hover:border-primary-300 transition-colors">
                  <i className="ri-mail-line text-foreground-400"></i>
                  {c.email}{c.preferred_contact === 'email' && ' ★'}
                </a>
              )}
              {c.phone && (
                <a href={`tel:${c.phone}`} className="text-xs flex items-center gap-1 text-foreground-600 bg-background-50 px-2.5 py-1 rounded-lg border border-background-200 hover:border-primary-300 transition-colors">
                  <i className="ri-phone-line text-foreground-400"></i>
                  {c.phone}{c.preferred_contact === 'phone' && ' ★'}
                </a>
              )}
              {c.telegram && (
                <span className="text-xs flex items-center gap-1 text-foreground-600 bg-background-50 px-2.5 py-1 rounded-lg border border-background-200">
                  <i className="ri-telegram-line text-foreground-400"></i>
                  {c.telegram}{c.preferred_contact === 'telegram' && ' ★'}
                </span>
              )}
              {c.whatsapp && (
                <span className="text-xs flex items-center gap-1 text-foreground-600 bg-background-50 px-2.5 py-1 rounded-lg border border-background-200">
                  <i className="ri-whatsapp-line text-foreground-400"></i>
                  {c.whatsapp}{c.preferred_contact === 'whatsapp' && ' ★'}
                </span>
              )}
              {c.location && (
                <span className="text-xs flex items-center gap-1 text-foreground-500 bg-background-50 px-2.5 py-1 rounded-lg border border-background-200">
                  <i className="ri-map-pin-line text-foreground-400"></i>
                  {c.location}
                </span>
              )}
              {c.english_level && (
                <span className="text-xs flex items-center gap-1 text-foreground-500 bg-background-50 px-2.5 py-1 rounded-lg border border-background-200">
                  <i className="ri-global-line text-foreground-400"></i>
                  Английский {c.english_level}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(c.tags || []).map((tag) => (
                <span key={tag} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                  {tagLabels[tag] || tag}
                </span>
              ))}
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                <i className="ri-download-cloud-line mr-1"></i>
                {c.resume_source}
              </span>
            </div>
          </div>
          <div className="w-full md:w-[320px] shrink-0">
            <ScoreBars
              resumeScore={rS ?? 0}
              answersScore={qS ?? 0}
              compatScore={compS ?? 0}
              totalScore={totalScore}
            />
          </div>
        </div>

        {/* Решение HR */}
        <div className="mt-6 pt-5 border-t border-background-200">
          <p className="text-xs font-bold text-foreground-400 uppercase tracking-wide mb-3">
            Решение по кандидату
            {c.hr_status === 'meeting' && c.meeting_at && (
              <span className="ml-2 normal-case tracking-normal text-amber-600 font-bold">
                <i className="ri-calendar-event-line mr-1" />
                Встреча: {new Date(c.meeting_at).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {c.hr_status === 'declined' && <span className="ml-2 normal-case tracking-normal text-red-500 font-bold">Отказ</span>}
          </p>
          <div className="flex items-start gap-2.5 flex-wrap">
            <select
              value={hrDraft}
              onChange={(e) => setHrDraft(e.target.value)}
              className="px-3.5 py-2.5 rounded-xl bg-white border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer min-w-[190px]"
            >
              <option value="none">Не обработан</option>
              <option value="meeting">Назначена встреча</option>
              <option value="declined">Отказ / неинтересен</option>
            </select>
            {hrDraft === 'meeting' && (
              <input
                type="datetime-local"
                value={meetingDraft}
                onChange={(e) => setMeetingDraft(e.target.value)}
                className="px-3 py-2.5 rounded-xl border border-amber-300 bg-amber-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            )}
            <input
              type="text"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Комментарий: что решили по кандидату..."
              className="flex-1 min-w-[220px] px-3.5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <button
              onClick={saveHrDecision}
              disabled={hrSaving}
              className="px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {hrSaving ? 'Сохраняю...' : 'Сохранить решение'}
            </button>
            {hrSaved && <span className="text-xs font-bold text-emerald-600 self-center"><i className="ri-check-line" /> Сохранено</span>}
          </div>
          {c.hr_note && (
            <p className="text-xs text-foreground-400 mt-2">Текущий комментарий: <span className="text-foreground-700">{c.hr_note}</span></p>
          )}
        </div>

        {/* Собеседование */}
        <div className="mt-6 pt-5 border-t border-background-200">
          <p className="text-xs font-bold text-foreground-400 uppercase tracking-wide mb-3">
            Собеседование
          </p>

          {interview ? (
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-300">
                <i className="ri-calendar-check-line text-amber-600"></i>
                <div>
                  <p className="text-sm font-semibold text-amber-900 capitalize">
                    {new Date(interview.starts_at).toLocaleString('ru-RU', {
                      weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                  <p className="text-[11px] text-amber-700">
                    {interview.duration_min} мин
                    {interview.location ? ` · ${interview.location}` : ''}
                    {interview.interviewer_name ? ` · ${interview.interviewer_name}` : ''}
                  </p>
                </div>
              </div>
              {interview.candidate_note && (
                <p className="text-xs text-foreground-500 self-center max-w-[280px]">
                  Комментарий кандидата: <span className="text-foreground-700">{interview.candidate_note}</span>
                </p>
              )}
              <button
                onClick={handleCancelInterview}
                disabled={inviting}
                className="px-4 py-2.5 rounded-xl text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                Отменить встречу
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-2.5 flex-wrap">
              <button
                onClick={() => handleInvite(true)}
                disabled={inviting}
                className="px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-2 whitespace-nowrap"
              >
                <i className={inviting ? 'ri-loader-4-line animate-spin' : 'ri-mail-send-line'}></i>
                Пригласить на интервью
              </button>
              <button
                onClick={() => handleInvite(false)}
                disabled={inviting}
                className="px-4 py-2.5 rounded-xl bg-white border border-background-200 text-sm text-foreground-700 hover:border-primary-400 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-link"></i>
                {linkCopied ? 'Ссылка скопирована' : 'Скопировать ссылку'}
              </button>
              <button
                onClick={() => navigate('/interviews')}
                className="px-4 py-2.5 rounded-xl text-sm text-foreground-500 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Мои окна
              </button>
            </div>
          )}

          {invite && !interview && (
            <div className="mt-3 space-y-1.5">
              {invite.email_sent && (
                <p className="text-xs text-emerald-700">
                  <i className="ri-check-line mr-1" />
                  Письмо со ссылкой отправлено{c.email ? ` на ${c.email}` : ''}
                </p>
              )}
              {invite.warning && (
                <p className="text-xs text-amber-700">
                  <i className="ri-error-warning-line mr-1" />
                  {invite.warning}
                </p>
              )}
              <p className="text-xs text-foreground-400 break-all">
                Ссылка кандидата:{' '}
                <a href={invite.link} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  {invite.link}
                </a>
              </p>
              <p className="text-[11px] text-foreground-400">
                Свободных окон: {invite.free_slots}
              </p>
            </div>
          )}
        </div>

        {/* Status tracker */}
        <div className="mt-8 pt-6 border-t border-background-200">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {statusFlow.map((s, i) => {
              const isDone = i <= currentStatusIndex;
              const isCurrent = i === currentStatusIndex;
              return (
                <div key={s.key} className="flex items-center shrink-0">
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isCurrent
                      ? 'bg-primary-500 text-white'
                      : isDone
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-background-100 text-foreground-400'
                  }`}>
                    {isDone && !isCurrent && (
                      <i className="ri-check-line text-[10px]"></i>
                    )}
                    {isCurrent && (
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                    )}
                    {s.label}
                  </div>
                  {i < statusFlow.length - 1 && (
                    <div className={`w-4 h-px mx-1 ${isDone ? 'bg-primary-300' : 'bg-background-300'}`}></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-background-200">
        <div className="flex items-center border-b border-background-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {tab.key === 'chat' && chatMessages.length > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">{chatMessages.length}</span>
              )}
            </button>
          ))}
          {canRescore && (
            <button
              onClick={handleRescore}
              disabled={rescoring}
              className="ml-auto mr-4 text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-200 text-foreground-600 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
            >
              <i className={rescoring ? 'ri-loader-4-line animate-spin' : 'ri-sparkling-line'}></i>
              {rescoring ? 'AI оценивает...' : 'Переоценить'}
            </button>
          )}
        </div>

        <div className="p-6 md:p-8">
          {/* Resume Tab */}
          {activeTab === 'resume' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-heading font-semibold text-foreground-900">Резюме кандидата</h3>
                {c.hh_resume_id !== undefined || c.resume_text ? (
                  <button
                    onClick={openResume}
                    disabled={resumeOpening}
                    className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg border border-background-200 text-foreground-600 hover:border-primary-400 hover:text-primary-700 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    <i className={resumeOpening ? 'ri-loader-4-line animate-spin' : 'ri-download-line'}></i>
                    Открыть PDF
                  </button>
                ) : null}
              </div>
              {!!personalQuestions.length && (
                <div className="bg-white border border-primary-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-question-answer-line text-primary-600" />
                    <h3 className="text-sm font-semibold text-foreground-900">
                      Вопросы по этому резюме ({personalQuestions.length})
                    </h3>
                  </div>
                  <p className="text-xs text-foreground-500 mb-4">
                    AI прочитал резюме и составил вопросы лично этому кандидату — они добавлены в его анкету
                    после общих вопросов
                  </p>
                  <div className="space-y-3">
                    {personalQuestions.map((q, i) => (
                      <div key={q.id || i} className="bg-background-50 rounded-lg border border-background-200 p-4">
                        <p className="text-sm text-foreground-800 leading-relaxed">{q.question}</p>
                        {q.reason && (
                          <p className="text-xs text-amber-700 mt-2">
                            <span className="font-medium">Что зацепило:</span> {q.reason}
                          </p>
                        )}
                        {q.eval_criteria && (
                          <p className="text-xs text-foreground-500 mt-1.5">
                            <span className="font-medium">На что смотреть в ответе:</span> {q.eval_criteria}
                          </p>
                        )}
                        {c.answers?.[q.id] && (
                          <div className="mt-3 pt-3 border-t border-background-200">
                            <p className="text-xs font-medium text-foreground-600 mb-1">Ответ кандидата</p>
                            <p className="text-sm text-foreground-800 whitespace-pre-wrap">{c.answers[q.id]}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {c.cover_letter && (
                <div className="bg-accent-50/50 border border-accent-200/50 rounded-xl p-4">
                  <p className="text-xs font-medium text-accent-800 mb-2 flex items-center gap-1.5">
                    <i className="ri-mail-open-line"></i>
                    Сопроводительное письмо (hh.ru)
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-wrap">{c.cover_letter}</p>
                </div>
              )}
              {scoring?.resume_score && (
                <>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-sparkling-line text-purple-600"></i>
                      <p className="text-sm font-semibold text-purple-900">AI-оценка резюме: {rS}/100</p>
                    </div>
                    <p className="text-sm text-purple-800 leading-relaxed">{scoring.resume_score.ai_summary}</p>
                    {!!scoring.resume_score.red_flags?.length && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {scoring.resume_score.red_flags.map((f) => (
                          <span key={f} className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {resumeCriteria.map((cr) => {
                      const score = scoring.resume_score!.criteria_scores[cr.id] ?? 0;
                      const pct = cr.weight ? (score / cr.weight) * 100 : 0;
                      return (
                        <div key={cr.id} className="bg-background-50 rounded-lg p-4 border border-background-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-foreground-700">{cr.name}</p>
                            <span className="text-xs font-bold text-purple-700">{score}/{cr.weight}</span>
                          </div>
                          <div className="h-1.5 rounded-full overflow-hidden bg-purple-100 mb-2">
                            <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${pct}%` }}></div>
                          </div>
                          {scoring.resume_score!.criteria_reasoning?.[cr.id] && (
                            <p className="text-xs text-foreground-500 leading-relaxed">{scoring.resume_score!.criteria_reasoning[cr.id]}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {c.resume_text ? (
                <div className="bg-background-50 rounded-xl p-5 border border-background-200">
                  <pre className="text-sm text-foreground-800 leading-relaxed whitespace-pre-wrap font-sans">{c.resume_text}</pre>
                </div>
              ) : (
                <p className="text-sm text-foreground-400">Резюме не загружено</p>
              )}
            </div>
          )}

          {/* Questionnaire Tab */}
          {activeTab === 'questionnaire' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-heading font-semibold text-foreground-900">Ответы на анкету</h3>
                <span className="text-xs text-foreground-500">{questions.length} вопросов</span>
              </div>
              {!c.answers && (
                <p className="text-sm text-foreground-400">Кандидат ещё не заполнил анкету</p>
              )}
              <div className="space-y-5">
                {questions.map((q, i) => {
                  const answer = c.answers?.[q.id];
                  const qScore = scoring?.answers_score?.criteria_scores?.[q.id];
                  return (
                    <div key={q.id} className="bg-background-50 rounded-xl p-5 border border-background-200">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 text-xs font-bold text-primary-700 mt-0.5">
                            {i + 1}
                          </span>
                          <p className="text-sm font-medium text-foreground-900 leading-relaxed">{q.question}</p>
                        </div>
                        {qScore !== undefined && (
                          <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700">
                            {qScore}/{q.weight}
                          </span>
                        )}
                      </div>
                      {answer ? (
                        <div className="ml-9">
                          <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-wrap">{answer}</p>
                          {scoring?.answers_score?.criteria_reasoning?.[q.id] && (
                            <div className="mt-3 p-3 rounded-lg bg-accent-50/50 border border-accent-200/50">
                              <p className="text-xs text-accent-800 font-medium mb-1">AI-оценка:</p>
                              <p className="text-xs text-foreground-600 leading-relaxed">{scoring.answers_score.criteria_reasoning[q.id]}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="ml-9">
                          <p className="text-xs text-foreground-400 italic">Ответ не получен</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {scoring?.answers_score?.ai_summary && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-sparkling-line text-emerald-600"></i>
                    <p className="text-sm font-semibold text-emerald-900">AI-анализ ответов: {qS}/100</p>
                  </div>
                  <p className="text-sm text-emerald-800 leading-relaxed">{scoring.answers_score.ai_summary}</p>
                  {!!scoring.answers_score.red_flags?.length && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {scoring.answers_score.red_flags.map((f) => (
                        <span key={f} className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{f}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Compatibility Tab */}
          {activeTab === 'compatibility' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-heading font-semibold text-foreground-900">Совместимость с руководителем</h3>
                {compS !== null && (
                  <span className="text-2xl font-heading font-bold text-amber-600">{compS}<span className="text-sm text-foreground-400">/100</span></span>
                )}
              </div>
              {!scoring?.compatibility_score && (
                <p className="text-sm text-foreground-400">Совместимость ещё не оценена. Нажмите «Переоценить», чтобы запустить AI-анализ.</p>
              )}
              {(scoring?.compatibility_scores?.length ? scoring.compatibility_scores : (scoring?.compatibility_score ? [scoring.compatibility_score as CompatBlock] : [])).map((comp, ci, arr) => (
                <div key={ci} className="space-y-4">
                  {arr.length > 1 && (
                    <div className="flex items-center gap-2 pt-2">
                      <span className="text-sm font-heading font-bold text-foreground-800">
                        <i className="ri-user-star-line mr-1.5 text-amber-500" />
                        {comp.profile_name || `Руководитель ${ci + 1}`}
                      </span>
                      <span className="text-lg font-heading font-bold text-amber-600">{comp.total_score}<span className="text-xs text-foreground-400">/100</span></span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {Object.entries(comp.criteria_scores).map(([key, score]) => {
                      const pct = (Number(score) / 20) * 100;
                      return (
                        <div key={key} className="bg-background-50 rounded-xl p-4 border border-background-200 text-center">
                          <p className="text-xs text-foreground-500 mb-2">{compatLabels[key] || key}</p>
                          <div className="text-2xl font-heading font-bold text-amber-600 mb-2">{score}<span className="text-sm text-foreground-400">/20</span></div>
                          <div className="h-2 rounded-full overflow-hidden bg-amber-100">
                            <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {comp.ai_summary && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-sparkling-line text-amber-600"></i>
                        <p className="text-sm font-semibold text-amber-900">AI-анализ совместимости{comp.profile_name ? ` — ${comp.profile_name}` : ''}</p>
                      </div>
                      <p className="text-sm text-amber-800 leading-relaxed">{comp.ai_summary}</p>
                      {!!comp.red_flags?.length && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-red-700 mb-1.5">Красные флаги</p>
                          <div className="flex flex-wrap gap-2">
                            {comp.red_flags.map((f) => (
                              <span key={f} className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {!!comp.strengths?.length && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-emerald-800 mb-1.5">Сильные стороны</p>
                          <div className="flex flex-wrap gap-2">
                            {comp.strengths.map((f) => (
                              <span key={f} className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* На чём построена оценка — чтобы расчёт не выглядел чёрным ящиком */}
                  <div className="bg-white border border-background-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-foreground-700 mb-2">На чём построена оценка</p>
                    <ul className="text-xs text-foreground-500 space-y-1 list-disc list-inside">
                      <li>Портрет руководителя{comp.profile_name ? `: ${comp.profile_name}` : ''} — его ответы про стиль работы и критичные требования</li>
                      <li>{c.resume_text ? 'Текст резюме кандидата' : 'Резюме отсутствует — оценка только по анкете'}</li>
                      <li>
                        {answersCount > 0
                          ? `Ответы кандидата на анкету: ${answersCount}`
                          : 'Анкета не заполнена — при нехватке данных модель ставит нейтральные баллы'}
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(comp.criteria_reasoning || {}).map(([key, text]) => (
                      <div key={key} className="bg-background-50 rounded-lg p-4 border border-background-200">
                        <p className="text-xs font-semibold text-foreground-700 mb-1">{compatLabels[key] || key}</p>
                        <p className="text-xs text-foreground-500 leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {false && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {Object.entries(scoring.compatibility_score.criteria_scores).map(([key, score]) => {
                      const pct = (Number(score) / 20) * 100;
                      return (
                        <div key={key} className="bg-background-50 rounded-xl p-4 border border-background-200 text-center">
                          <p className="text-xs text-foreground-500 mb-2">{compatLabels[key] || key}</p>
                          <div className="text-2xl font-heading font-bold text-amber-600 mb-2">{score}<span className="text-sm text-foreground-400">/20</span></div>
                          <div className="h-2 rounded-full overflow-hidden bg-amber-100">
                            <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {scoring.compatibility_score.ai_summary && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-sparkling-line text-amber-600"></i>
                        <p className="text-sm font-semibold text-amber-900">AI-анализ совместимости</p>
                      </div>
                      <p className="text-sm text-amber-800 leading-relaxed">{scoring.compatibility_score.ai_summary}</p>
                      {!!scoring.compatibility_score.red_flags?.length && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {scoring.compatibility_score.red_flags.map((f) => (
                            <span key={f} className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{f}</span>
                          ))}
                        </div>
                      )}
                      {!!scoring.compatibility_score.strengths?.length && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {scoring.compatibility_score.strengths.map((s) => (
                            <span key={s} className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-3">
                    {Object.entries(scoring.compatibility_score.criteria_reasoning || {}).map(([key, text]) => (
                      <div key={key} className="bg-background-50 rounded-lg p-4 border border-background-200">
                        <p className="text-xs font-semibold text-foreground-700 mb-1">{compatLabels[key] || key}</p>
                        <p className="text-xs text-foreground-500 leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-heading font-semibold text-foreground-900">Переписка в hh.ru</h3>
                <button
                  onClick={reloadChat}
                  disabled={syncing}
                  className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className={syncing ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}></i>
                  Синхронизировать
                </button>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200 p-4 max-h-[500px] overflow-y-auto space-y-3">
                {chatMessages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.author === 'employer' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] md:max-w-[60%] rounded-2xl px-4 py-3 ${
                      msg.author === 'employer'
                        ? 'bg-primary-500 text-white rounded-br-md'
                        : 'bg-white border border-background-200 text-foreground-800 rounded-bl-md'
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                      <p className={`text-[11px] mt-1 ${msg.author === 'employer' ? 'text-primary-200' : 'text-foreground-400'}`}>
                        {formatDate(msg.sent_at)}
                      </p>
                    </div>
                  </div>
                ))}
                {chatMessages.length === 0 && (
                  <p className="text-sm text-foreground-400 text-center py-6">
                    {c.hh_resume_id ? 'Сообщений пока нет' : 'Кандидат не с hh.ru — чата нет'}
                  </p>
                )}
              </div>
              {c.hh_resume_id && (
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Написать сообщение..."
                    className="flex-1 px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  />
                  <button
                    type="submit"
                    disabled={sending || !chatInput.trim()}
                    className="px-5 py-3 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    {sending ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                    Отправить
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Emails Tab */}
          {activeTab === 'emails' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-heading font-semibold text-foreground-900">История писем</h3>
              </div>
              {emailLog.length === 0 && (
                <p className="text-sm text-foreground-400">Писем не отправлялось</p>
              )}
              <div className="space-y-2">
                {emailLog.map((log, i) => {
                  const statusConfig: Record<string, { icon: string; color: string; label: string }> = {
                    sent: { icon: 'ri-send-plane-line', color: 'text-foreground-500', label: 'Отправлено' },
                    delivered: { icon: 'ri-check-double-line', color: 'text-emerald-600', label: 'Доставлено' },
                    opened: { icon: 'ri-eye-line', color: 'text-primary-600', label: 'Открыто' },
                    clicked: { icon: 'ri-cursor-line', color: 'text-accent-600', label: 'Клик' },
                    bounced: { icon: 'ri-error-warning-line', color: 'text-red-600', label: 'Ошибка' },
                  };
                  const cfg = statusConfig[log.status] || statusConfig.sent;
                  const typeLabels: Record<string, string> = {
                    questionnaire: 'Анкета',
                    interview: 'Интервью',
                    rejection: 'Отказ',
                  };
                  return (
                    <div key={i} className="flex items-center gap-4 p-4 bg-background-50 rounded-xl border border-background-200">
                      <div className={`w-9 h-9 rounded-full bg-white border border-background-200 flex items-center justify-center shrink-0 ${cfg.color}`}>
                        <i className={cfg.icon}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-800">
                          {typeLabels[log.type] || log.type}
                          <span className="text-xs text-foreground-400 font-normal ml-2">{cfg.label}</span>
                        </p>
                        <p className="text-xs text-foreground-400">{formatDate(log.sent_at || log.created_at)}</p>
                      </div>
                      <span className={`text-[11px] font-medium px-2 py-1 rounded-full ${
                        log.status === 'bounced' ? 'bg-red-50 text-red-700' :
                        log.status === 'clicked' ? 'bg-accent-50 text-accent-700' :
                        log.status === 'opened' ? 'bg-primary-50 text-primary-700' :
                        log.status === 'delivered' ? 'bg-emerald-50 text-emerald-700' :
                        'bg-background-100 text-foreground-500'
                      }`}>
                        {cfg.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
